import type { CurrentZoom } from './zoom';
import type { ZoomRangeWithBlocks } from './zoom2.svelte.js';

export type Cell = {
    startTimestamp: number;
    endTimestamp: number;
    label: string;
};

export type Zoom = {
    getLabel: (date: Date) => string;
    cellStepSec: number;
    cellLabels: (startDate: Date) => string[];
    cellWidth: number;
};
export type Block = {
    label: string;
    cells: Cell[];
};
export const getBlockDates = ({ startDate, endDate, zoom }: { startDate: Date; endDate: Date; zoom: Zoom }) => {
    const dates: Date[] = [];
    const currDate = new Date(startDate);
    while (currDate < endDate) {
        dates.push(new Date(currDate));
        currDate.setSeconds(currDate.getSeconds() + zoom.cellStepSec * zoom.cellLabels.length);
    }
    return dates;
};

export const getBlocks: (input: {
    startDate: Date;
    endDate: Date;
    setBlockEnd: (date: Date) => void;
    getBlockLabel: (date: Date) => string;
    cellStep: (date: Date) => void;
    getCellLabel: (date: Date) => string;
}) => Block[] = ({ startDate, endDate, setBlockEnd, getBlockLabel, cellStep, getCellLabel }) => {
    console.time('getBlocks');
    const currDate = new Date(startDate);
    const blocks: Block[] = [];
    while (currDate < endDate) {
        // console.log({ currDate });
        const blockEndDate = new Date(currDate);
        setBlockEnd(blockEndDate);
        blocks.push({
            label: getBlockLabel(currDate),
            cells: getCells(currDate, blockEndDate, cellStep, getCellLabel),
        });
    }
    console.timeEnd('getBlocks');
    return blocks;
};

export const getCells: (startDate: Date, endDate: Date, cellStep: (date: Date) => void, getLabel: (date: Date) => string) => Cell[] = (
    startDate,
    endDate,
    cellStep,
    getLabel,
) => {
    const currDate = startDate;
    const cells: Cell[] = [];
    while (currDate < endDate) {
        // console.log({ currDate, fn: 'getCells' });
        const startTimestamp = currDate.getTime();
        const label = getLabel(currDate);
        cellStep(currDate);
        cells.push({ label, startTimestamp, endTimestamp: currDate.getTime() });
    }
    return cells;
};

export const getUpperLowerCells = (start: Date, currentZoom: CurrentZoom) => {
    const searchTimestamp = start.getTime();

    const blockI = currentZoom.blocks.findIndex((block) => {
        const firstCell = block.cells[0];
        if (firstCell === undefined) return false;
        const lastCell = block.cells[block.cells.length - 1];
        if (firstCell === lastCell) return false;

        return firstCell.startTimestamp <= searchTimestamp && lastCell.endTimestamp >= searchTimestamp;
    });
    const foundBlock = currentZoom.blocks[blockI];
    if (foundBlock === undefined) {
        console.warn(`Couldnt find the block for date: ${start}`);
        return null;
    }

    // found the correct block, now we need to find the correct cells and lerp between the two
    const foundCellI = foundBlock.cells.findIndex((cell) => {
        return cell.endTimestamp >= searchTimestamp;
    });

    let blockOffset = 0;
    for (let i = 0; i < blockI; i++) {
        blockOffset += currentZoom.blocks[i].cells.length * currentZoom.cellStepWidthPx;
    }
    return {
        blockI,
        foundCellI,
        blockOffset,
    };
};
export const getXFromStart = (start: Date, currentZoom: ZoomRangeWithBlocks) => {
    const searchTimestamp = start.getTime();
    if (start <= currentZoom.blocks[0]?.start) return 0;
    const foundCells = getUpperLowerCells(start, currentZoom);
    if (foundCells === null) return null;
    const { foundCellI, blockI, blockOffset } = foundCells;
    // console.log({ start, foundCellI, blockI, blockOffset });
    const foundBlock = currentZoom.blocks[blockI];

    const foundCell = foundBlock.cells[foundCellI];
    if (foundCell?.startTimestamp === searchTimestamp) return Math.floor(blockOffset + foundCellI * currentZoom.cellStepWidthPx);
    if (foundCell?.endTimestamp === searchTimestamp) return Math.floor(blockOffset + (foundCellI + 1) * currentZoom.cellStepWidthPx);

    if (foundCell === undefined) {
        console.warn('found cell was undefined');
        return null;
    }

    // not at a cell start or end so we need to lerp between cells

    const cellDuration = foundCell.endTimestamp - foundCell.startTimestamp;
    const additionFromLower = searchTimestamp - foundCell.startTimestamp;
    const lerp = additionFromLower / cellDuration;
    // console.log({ diff: cellDuration, additionFromLower, lerp });
    return Math.floor(blockOffset + (lerp + foundCellI) * currentZoom.cellStepWidthPx);
};

export const getPxFromSeconds = (start: Date, durationSecs: number, zoom: ZoomRangeWithBlocks) => {
    const startX = getXFromStart(start, zoom);
    const endDate = new Date(start);
    endDate.setSeconds(endDate.getSeconds() + durationSecs);
    const endX = getXFromStart(endDate, zoom);
    if (startX === null || endX === null) return 0;
    return endX - startX;
};

export const getDateFromPx = (x: number, blocks: Block[], cellStepWidthPx: number) => {
    const cellsIFloat = x / cellStepWidthPx;
    const cellI = Math.floor(cellsIFloat);
    let currCellI = 0;
    let foundCell: Cell | null = null;
    blocksLoop: for (const block of blocks) {
        for (const cell of block.cells) {
            if (currCellI === cellI) {
                foundCell = cell;
                break blocksLoop;
            }
            currCellI++;
        }
    }

    if (foundCell === null) {
        return null;
    }

    // found the correct cell, now lerp between diff
    const diff = cellsIFloat - cellI;

    const timestampDiff = foundCell.endTimestamp - foundCell.startTimestamp;

    const valMs = foundCell.startTimestamp + timestampDiff * diff;
    // console.log({ diff, timestampDiff, valMs, cellsIFloat });
    return new Date(valMs);
};

export const getPxPerMs = (blocks: Block[], cellStepWidthPx: number) => {
    const block = blocks[0];
    if (block === undefined) {
        console.warn('called getPxPerMs with empty blocks, requires at least one block to calculate the width');
        return 0;
    }

    const cell = block.cells[0];
    if (cell === undefined) {
        console.warn('called getPxPerMs with empty cells, requires at least one cell to calculate the width');
        return 0;
    }

    return cellStepWidthPx / (cell.endTimestamp - cell.startTimestamp);
};

export const isJobInDisplayRange = ({ zoom, jobDurationSec, jobStart }: { zoom: ZoomRangeWithBlocks; jobStart: Date; jobDurationSec: number }) => {
    const jobEnd = new Date(jobStart);
    jobEnd.setSeconds(jobEnd.getSeconds() + jobDurationSec);

    return jobEnd > zoom.blocks[0].start && jobStart <= zoom.blocks[zoom.blocks.length - 1]?.end;
};

// new things
//
//

export const getDurationSecInPx = (zoom: ZoomRangeWithBlocks, durationSec: number) => {
    const val = zoom.cellPx * ((durationSec * 1000) / zoom.zoomRangePreset.cellMs);
    return val;
};

export const getPxFromStart = (zoom: ZoomRangeWithBlocks, start: Date) => {
    const firstDateMs = zoom.blocks[0].start.getTime();
    const startMs = start.getTime();
    const diffMs = startMs - firstDateMs;
    return zoom.cellPx * (diffMs / zoom.zoomRangePreset.cellMs);
};
